import type { ApiError } from '~/types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

/**
 * Global error listener for API failures.
 * Used by analytics to track client-side errors without coupling
 * the API client to analytics directly.
 */
type ApiErrorListener = (endpoint: string, statusCode: number, message: string) => void;
const errorListeners: ApiErrorListener[] = [];

export function onApiError(listener: ApiErrorListener): () => void {
  errorListeners.push(listener);
  return () => {
    const index = errorListeners.indexOf(listener);
    if (index !== -1) errorListeners.splice(index, 1);
  };
}

function notifyErrorListeners(endpoint: string, statusCode: number, message: string): void {
  for (const listener of errorListeners) {
    try {
      listener(endpoint, statusCode, message);
    } catch {
      // Error listeners should never break API client
    }
  }
}

/**
 * Custom error class for API errors with status code and structured message.
 */
export class ApiRequestError extends Error {
  public statusCode: number;
  public errorBody: ApiError | null;

  constructor(statusCode: number, message: string, errorBody: ApiError | null = null) {
    super(message);
    this.name = 'ApiRequestError';
    this.statusCode = statusCode;
    this.errorBody = errorBody;
  }
}

interface FetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  token?: string | null;
}

/**
 * API client fetch wrapper.
 * - Configurable base URL via VITE_API_BASE_URL
 * - JSON request/response handling
 * - Optional auth header injection
 * - Structured error handling with ApiRequestError
 */
export async function apiClient<T>(
  endpoint: string,
  options: FetchOptions = {},
): Promise<T> {
  const { body, token, headers: customHeaders, ...fetchOptions } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...((customHeaders as Record<string, string>) || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = `${API_BASE_URL}${endpoint}`;

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    let errorBody: ApiError | null = null;
    try {
      errorBody = await response.json() as ApiError;
    } catch {
      // Response body may not be JSON
    }

    const errorMessage = errorBody?.message || `API request failed with status ${response.status}`;
    notifyErrorListeners(endpoint, response.status, errorMessage);

    throw new ApiRequestError(
      response.status,
      errorMessage,
      errorBody,
    );
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}
